import debug from 'debug';
import { v4 as uuidv4 } from 'uuid';
import router from '@/router';
import globals from '@/globals';
import DateLib from '@/lib/date';
import preQualifyService from './preQualify.service';
import preQualifyLib from './preQualify.lib';
import eventService from '../../services/event';
import getHandler from '../../lib/handleEvent';
import logger from '../../lib/logger';

const skeletonTimeout = globals().skeleton.checkout.timeout;

const log = debug('store:preQualify');

const handleEvent = getHandler(log);

function calculatePrice({ productValue, installments, interest, downPaymentPercentage }) {
  const downPaymentValue = ((downPaymentPercentage / 100) * productValue);
  const pv = productValue - downPaymentValue;
  const pmt = pv * ((((1 + interest) ** installments) * interest)
    / (((1 + interest) ** installments) - 1));
  const financedAmount = pmt * installments;
  return {
    pmt,
    downPaymentValue,
    financedAmount,
    totalAmount: financedAmount + downPaymentValue,
  };
}

function getBestPricing({ result, preview }) {
  if (['PREAPPROVED', 'APPROVED'].includes(result)
    && Array.isArray(preview.downPaymentOptions)
    && preview.downPaymentOptions.length > 0) {
    const lastDpOpt = preview.downPaymentOptions[preview.downPaymentOptions.length - 1];
    if (Array.isArray(lastDpOpt.installments) && lastDpOpt.installments.length > 0) {
      const lastInstallment = lastDpOpt.installments[lastDpOpt.installments.length - 1];
      const { downPaymentPercentage } = lastDpOpt;
      const { interest } = lastInstallment;
      return {
        pmt: lastInstallment.value,
        installments: lastInstallment.installments,
        interest: parseFloat((interest / 100).toFixed(2)),
        downPaymentPercentage,
        downPayment: downPaymentPercentage > 0,
      };
    }
  }
  return null;
}
function getDefaultInstallments({ installments }) {
  if (installments.length > 3) {
    if (installments.length === 4) {
      return [
        installments[0],
        installments[2],
        installments[3],
      ];
    }
    if (installments.length === 5) {
      return [
        installments[0],
        installments[2],
        installments[4],
      ];
    }
    if (installments.length === 7) {
      return [
        installments[0],
        installments[3],
        installments[6],
      ];
    }
  }
  return installments;
}
function filterValue({ type, value }) {
  const filters = {
    phone: value && value.replace(/[() -]/g, ''),
    cpf: value && value.replace(/[.-]/g, ''),
    zipCode: value && value.replace('-', ''),
  };
  return filters[type] || value;
}

function fixed2(raw) {
  return parseFloat(raw.toFixed(2));
}

export default {
  namespaced: true,
  state: {
    apiKey: '',
    env: '',
    preQualifyId: null,
    sessionId: null,
    context: '',
    page: '',
    eventHandler: null,

    installmentsSkeleton: false,
    checkboxRememberCredit: false,

    productInfo: {},
    productSimulation: {},

    user: {},

    price: 0,

    downPaymentOptions: null,
    downPaymentIndexSelected: 0,
    installmentOptions: null,

    preApprove: null,
    cantPreApprove: false,
    cantPreApproveMaxTries: false,

    hasPreCondition: false,
  },
  actions: {
    async error({ state }) {
      handleEvent({ state, ev: 'onError' });
    },
    async ready({ state }) {
      handleEvent({ state, ev: 'onReady' });
    },
    async close({ state, commit }) {
      handleEvent({ state, ev: 'onClosed' });
      setTimeout(() => commit('goToPage', 'prequalify-simulation'), 500);
    },
    async goToCheckout({ state }) {
      handleEvent({ state, ev: 'onGoToCheckout' });
    },
    async openModal({ state, dispatch, commit }, data) {
      if (!state.apiKey && data && data.apiKey) {
        commit('setApiKey', data);
      }

      dispatch('emitEvent', {
        type: 'show',
      });
      if (state.preApprove) {
        if (['APPROVED', 'PREAPPROVED'].includes(state.preApprove.result)) {
          dispatch('emitEvent', {
            type: 'preapproved',
          });
        } else if (['DENIED', 'PREDENIED'].includes(state.preApprove.result)) {
          dispatch('emitEvent', {
            type: 'predenied',
          });
        }
      }
    },
    async init({ rootState, state, commit, dispatch }, data) {
      commit('setHasPreCondition', false);
      dispatch('theme/setTheme', 'default', { root: true });
      if (data.retailerName) {
        dispatch('theme/setTheme', data.retailerName, { root: true });
      }

      log('init', state);
      if (data.trackId && rootState.track.id !== data.trackId) {
        dispatch('track/setId', { id: data.trackId }, { root: true });
      }

      commit('setSessionId', data.sessionId);
      if (state.preApprove && state.preApprove.result) {
        if (
          state.preApprove.result === 'NO_PREAPPROVE'
          || state.preApprove.result === 'NO_PREAPPROVE_MAX_TRIES'
        ) {
          if (
            state.preApprove
            && state.preApprove.createdAtVuex
            && state.preApprove.result === 'NO_PREAPPROVE_MAX_TRIES'
            && DateLib.diffInHours(state.preApprove.createdAtVuex, new Date()) > 1
          ) {
            commit('setPreApprove', null);
          }

          if (state.preApprove.result === 'NO_PREAPPROVE') commit('setPreApprove', null);

          commit('setInitData', data);
          commit('goToPage', 'prequalify-simulation');
        } else if (state.preApprove.result !== 'DENIED') {
          dispatch('updateProductInfo', data.productInfo);
          commit('goToPage', 'prequalify-simulation');
        } else {
          commit('setHasPreCondition', true);
          commit('goToPage', 'prequalify-reproved');
        }
      } else if (data.preQualifyId && data.preQualifyId === state.preQualifyId) {
        dispatch('updateProductInfo', data.productInfo);
        commit('goToPage', state.page);
      } else {
        commit('setInitData', data);
        commit('goToPage', 'prequalify-simulation');
      }
      if (data.emitShow) dispatch('openModal', {});
    },
    async goToPage({ commit }, page) {
      commit('goToPage', page);
    },
    async getPreApprove({ rootState, state, commit }) {
      const trackId = rootState.track.id;

      const { data } = await preQualifyService
        .preApprove({
          apiKey: state.apiKey,
          env: state.env,
          cpf: state.user.cpf,
          phone: state.user.phone,
          email: state.user.email,
          zipCode: state.user.zipCode,
          price: state.price,
          trackId,
        })
        .catch((err) => {
          if (err.response.status === 400) {
            throw new Error(err.response.data);
          }

          commit('goToPage', 'checkout-error');
        });
      const { result, refusalReason, preApprovedCredit, preview, error } = data;

      if (error) {
        log('preApprove error', error);
        commit('goToPage', 'checkout-error');
      }

      const bestPricing = getBestPricing({ result, preview, state });

      commit('setPreApprove', {
        showScript: preview && preview.showScript,
        preApproved: preview && preview.preApproved,
        policyConfigurationId: preApprovedCredit
          && preApprovedCredit.policyConfigurationId
          && preApprovedCredit.policyConfigurationId.toString(),
        maxCreditValue: (preApprovedCredit && preApprovedCredit.maxValue),
        bestPricing,
        result,
        refusalReason,
        createdAtVuex: new Date(),
      });

      commit('setProductSimulation', bestPricing);

      handleEvent({
        state,
        ev: 'onVerify',
        data: bestPricing,
      });
    },
    updatePreCondition({ commit }, data) {
      commit('setHasPreCondition', data);
    },
    async getPreview({ rootState, state, commit, dispatch }) {
      const previewRequest = {
        apiKey: state.apiKey,
        env: state.env,
        totalProductPrice: state.price,
        shipping: 0,
        zipCode: state.user && state.user.zipCode,
      };

      if (state.user && state.user.cpf) {
        previewRequest.cpf = state.user.cpf;
        previewRequest.email = state.user.email;
        previewRequest.phone = state.user.phone;
      }

      const currentTrackId = rootState.track && rootState.track.id;
      if (currentTrackId) previewRequest.trackId = currentTrackId;

      const { data } = await preQualifyService
        .preview(previewRequest)
        .catch((err) => {
          commit('goToPage', 'checkout-error');

          if (err && err.response && err.response.data) {
            throw new Error(err.response.data);
          }
        });

      const {
        downPaymentOptions,
        trackId,
      } = data;

      if (trackId && trackId !== currentTrackId) {
        dispatch('track/setId', { id: trackId }, { root: true });
      }

      commit('setDownPaymentOptions', downPaymentOptions);
    },
    async setContext({ commit }, context) {
      commit('setContext', context);
    },
    setDownPaymentIndex({ commit, state }, index) {
      const { summarySkeleton } = state;
      if (!summarySkeleton) {
        commit('toggleInstallmentsSkeleton', true);
        setTimeout(() => {
          commit('toggleInstallmentsSkeleton', false);
        }, skeletonTimeout);
      }
      commit('setDownPaymentIndex', index);
    },
    updatePrice({ commit, state }, price) {
      const { summarySkeleton, downPaymentOptions } = state;

      let firstPrice = 0;

      const newDpOpt = downPaymentOptions.map((dpOpt, i) => {
        const newOption = { ...dpOpt };
        const optionInstallment = dpOpt.installments[dpOpt.installments.length - 1];
        const { totalAmount } = calculatePrice({
          productValue: price,
          installments: optionInstallment.installments,
          interest: optionInstallment.interest / 100,
          downPaymentPercentage: dpOpt.downPaymentPercentage,
        });

        let economyValue = 0;
        if (i === 0) firstPrice = totalAmount;
        else economyValue = firstPrice - totalAmount;
        newOption.economyValue = parseFloat(economyValue.toFixed(2));
        newOption.downPaymentValue = (dpOpt.downPaymentPercentage / 100) * price;

        const installments = getDefaultInstallments({
          installments: dpOpt.installments,
        });

        newOption.installments = installments.map((installment) => {
          const interestForInstallment = installment.interest;
          const res = calculatePrice({
            productValue: price,
            installments: installment.installments,
            interest: interestForInstallment / 100,
            downPaymentPercentage: dpOpt.downPaymentPercentage,
          });

          return {
            value: res.pmt,
            total: res.totalAmount,
            installments: installment.installments,
            interest: parseFloat((interestForInstallment).toFixed(2)),
          };
        });

        return newOption;
      });

      if (!summarySkeleton) {
        commit('toggleInstallmentsSkeleton', true);
        setTimeout(() => {
          commit('toggleInstallmentsSkeleton', false);
        }, skeletonTimeout);
      }

      commit('setPrice', price);
      commit('setDownPaymentOptions', newDpOpt);
    },
    updateProductInfo({ commit }, productInfo) {
      commit('setPrice', productInfo.value);
      commit('setProductInfo', productInfo);
    },
    emitEvent({ rootState, state }, { type }) {
      eventService.logPdpEvents({
        type,
        data: {
          ...state,
          trackId: rootState.track && rootState.track.id,
        },
      }).catch(logger.error);
    },
    onResize({ commit }, height) {
      commit('setIframeHeight', height);
    },
  },
  mutations: {
    setHasPreCondition(state, hasPreCondition) {
      state.hasPreCondition = hasPreCondition;
    },
    toggleInstallmentsSkeleton(state, installmentsSkeleton) {
      state.installmentsSkeleton = installmentsSkeleton;
    },
    toggleCheckboxRememberCredit(state, checkboxRememberCredit) {
      state.checkboxRememberCredit = checkboxRememberCredit;
    },
    setPrice(state, price) {
      state.price = price;
    },
    setSessionId(state, sessionId) {
      state.sessionId = sessionId;
    },
    setDownPaymentOptions(state, downPaymentOptions) {
      state.downPaymentOptions = downPaymentOptions;
    },
    setInstallmentOptions(state, installmentOptions) {
      state.installmentOptions = installmentOptions;
    },
    setUserData(state, { type, value }) {
      handleEvent({ state, ev: 'onChangeUserData', data: { type, value } });
      state.user[type] = filterValue({ type, value });
    },
    goToPage(state, page) {
      handleEvent({ state, ev: 'onChangePage', data: { page } });
      router.replace({ name: page }).catch((err) => log('router error', err));
      state.page = page;
    },
    setContext(state, context) {
      const { eventHandler } = preQualifyLib.getContextVariables(context);
      state.eventHandler = eventHandler;
      state.context = context;
    },
    setDownPaymentIndex(state, index) {
      state.downPaymentIndexSelected = index;
    },
    setApiKey(state, { apiKey }) {
      state.apiKey = apiKey;
    },
    setInitData(state, { apiKey, env, productInfo }) {
      state.apiKey = apiKey;
      state.env = env;
      state.price = productInfo.value;
      state.productInfo = productInfo;
      state.preQualifyId = uuidv4();
      handleEvent({ state, ev: 'onSendPreQualifyId', data: { preQualifyId: state.preQualifyId } });
    },
    setIframeHeight(state, height) {
      handleEvent({ state, ev: 'onResize', data: { height } });
    },
    setPreApprove(state, preApprove) {
      state.preApprove = preApprove;
    },
    setProductInfo(state, productInfo) {
      state.productInfo = productInfo;
    },
    setProductSimulation(state, productSimulation) {
      state.productSimulation = productSimulation;
    },
  },
  getters: {
    isApproved: ({ preApprove }) => {
      if (!preApprove) return null;
      return preApprove.result === 'APPROVED' || preApprove.result === 'PREAPPROVED';
    },
    hasResult: ({ preApprove }) => preApprove && preApprove.result !== undefined,
    inputError: ({ preApprove }) => {
      const { result, createdAtVuex } = preApprove || {};

      if (result === 'NO_PREAPPROVE_MAX_TRIES'
        && (!createdAtVuex || DateLib.diffInHours(createdAtVuex, new Date()) < 1)
      ) {
        return 'maxTries';
      }
      if (result === 'NO_PREAPPROVE') return 'initialError';

      return null;
    },
    translateErrorText: (state, getters) => {
      const { inputError } = getters;
      const textOptions = {
        initialError: 'Não foi possível verificar seu crédito neste momento, tente novamente.',
        maxTries: 'Estamos tendo dificuldade em verificar seu crédito neste momento, tente após algumas horas.',
      };
      return textOptions[inputError];
    },
    infoDeniedMessage: ({ preApprove }) => {
      if (preApprove) {
        if (preApprove.result === 'NO_ACTION') {
          const { refusalReason } = preApprove;
          if (refusalReason === 'TOTALVALUE_LOW') {
            return 'Infelizmente não podemos seguir com a sua compra. Valor total abaixo do permitido.';
          }
          if (refusalReason === 'TOTALVALUE_HIGH') {
            return 'Infelizmente não podemos seguir com a sua compra. Valor total acima do permitido.';
          }
          if (refusalReason === 'DDD_NOT_FOUND') {
            return 'Verifique o campo telefone digitado e tente novamente.';
          }
          if (refusalReason === 'USER_HAS_TRANSACTION') {
            return 'Você já possui um financiamento ativo. Para efetuar uma nova compra você precisa finalizar a atual.';
          }
        }

        if (preApprove.result === 'PRICE_OUT_RANGE' && preApprove.preApprovedCredit && preApprove.preApprovedCredit.maxValue) {
          return `Infelizmente o limite liberado de R$ ${fixed2(preApprove.preApprovedCredit.maxValue)} não permite uma compra desse valor.`;
        }
      }

      return `Sua avaliação de crédito não foi aprovada no momento. Mas
        não se preocupe, você pode tentar novamente no futuro.`;
    },
  },
};
